import { getCurrentAppBrand } from '@b2ag/app-brands'
import { type AxiosInstance, createFrontAxiosClient } from '@b2ag/axios-client'
import { getConfig } from '@b2ag/micro-front-tools'
import { ref, type Ref } from 'vue'
import FeatureFlippingService, { type FeatureFlippingParam } from './featureFlipping.service'

let isProductViewedEnabled: Ref<boolean>
let isNewSearchEnabled: Ref<boolean>
let isAnchoredArticlesEnabled: Ref<boolean>
let isSearchBarPrevisionnelEnabled: Ref<boolean>
let isShippingAddressEnabled: Ref<boolean>
let isRealtimeStocksEnabled: Ref<boolean>
let isPrestaServiceEnabled: Ref<boolean>
let isReorderDisabled: Ref<boolean>
let isReorderV2Enabled: Ref<boolean>
let isSimilarProductsEnabled: Ref<boolean>
let isFrequentlyBoughtTogetherEnabled: Ref<boolean>
let isCustomFiltersEnabled: Ref<boolean>
let isDefaultCustomFiltersEnabled: Ref<boolean>
let features: Record<string, Ref>
let featureFlippingService: FeatureFlippingService | undefined
let isNewConfigurationEnabled: Ref<boolean>
let isSearchStockV2Enabled: Ref<boolean>
let isCancelConsignmentEnabled: Ref<boolean>
let useAllUserSegmentsForBestOffer: Ref<boolean>
let isNewPdpOffersEnabled: Ref<boolean>
let isFlashOrderEnabled: Ref<boolean>
let isMillingStoreCapabilityEnabled: Ref<boolean>
let isDocumentAccessRbacEnabled: Ref<boolean>
let isNewAssociationModeSelectorEnabled: Ref<boolean>

/**
 * @deprecated For unit test only
 */
export function reset() {
  featureFlippingService = undefined
}

export function provideFeatureFlipping(
  featureFlippingApiClient: Pick<AxiosInstance, 'post'>,
  appFront: string,
  appBrand: string = 'aladin',
) {
  featureFlippingService = new FeatureFlippingService(featureFlippingApiClient, appBrand)

  isProductViewedEnabled = ref(false)
  isNewSearchEnabled = ref(false)
  isAnchoredArticlesEnabled = ref(false)
  isSearchBarPrevisionnelEnabled = ref(false)
  isShippingAddressEnabled = ref(false)
  isRealtimeStocksEnabled = ref(false)
  isPrestaServiceEnabled = ref(false)
  isReorderDisabled = ref(false)
  isReorderV2Enabled = ref(false)
  isSimilarProductsEnabled = ref(false)
  isFrequentlyBoughtTogetherEnabled = ref(false)
  isCustomFiltersEnabled = ref(false)
  isDefaultCustomFiltersEnabled = ref(false)
  isNewConfigurationEnabled = ref(false)
  isSearchStockV2Enabled = ref(false)
  isCancelConsignmentEnabled = ref(false)
  useAllUserSegmentsForBestOffer = ref(false)
  isNewPdpOffersEnabled = ref(false)
  isFlashOrderEnabled = ref(false)
  isMillingStoreCapabilityEnabled = ref(false)
  isDocumentAccessRbacEnabled = ref(false)
  isNewAssociationModeSelectorEnabled = ref(false)

  features = {
    [`${appBrand}:front-tech:products-viewed`]: isProductViewedEnabled,
    [`${appBrand}:front-tech:taskforce-new-search`]: isNewSearchEnabled,
    [`${appBrand}:article:anchored-articles`]: isAnchoredArticlesEnabled,
    [`${appBrand}:previsionnel:search-bar`]: isSearchBarPrevisionnelEnabled,
    [`${appBrand}:shipping-address`]: isShippingAddressEnabled,
    [`${appBrand}:front-${appFront}:realtime-stocks`]: isRealtimeStocksEnabled,
    [`${appBrand}:presta-service`]: isPrestaServiceEnabled,
    [`${appBrand}:front-${appFront}:disable-reorder`]: isReorderDisabled,
    [`${appBrand}:front-${appFront}:enable-reorder-v2`]: isReorderV2Enabled,
    [`${appBrand}:similar-products-from-algolia`]: isSimilarProductsEnabled,
    [`${appBrand}:frequently-bought-products-from-algolia`]: isFrequentlyBoughtTogetherEnabled,
    [`${appBrand}:custom-filters-configuration`]: isCustomFiltersEnabled,
    [`${appBrand}:default-custom-filters-configuration`]: isDefaultCustomFiltersEnabled,
    [`${appBrand}:new-configuration`]: isNewConfigurationEnabled,
    [`${appBrand}:search-stocks-v2`]: isSearchStockV2Enabled,
    [`${appBrand}:front-${appFront}:enable-cancel-consignment`]: isCancelConsignmentEnabled,
    [`${appBrand}:use-all-user-segments-for-best-offer`]: useAllUserSegmentsForBestOffer,
    [`${appBrand}:front-${appFront}:new-pdp-offers`]: isNewPdpOffersEnabled,
    [`${appBrand}:flash-order`]: isFlashOrderEnabled,
    [`${appBrand}:milling-store-capability`]: isMillingStoreCapabilityEnabled,
    [`${appBrand}:document-access-rbac`]: isDocumentAccessRbacEnabled,
    [`${appBrand}:front-${appFront}:new-association-mode-selector`]: isNewAssociationModeSelectorEnabled,
  }
}

export function checkFeatures(partnerId?: number): Promise<FeatureFlippingParam[]> {
  if (!featureFlippingService) throw new Error('FeatureFlippingService is not initialized')
  return featureFlippingService.checkAllFeatures(partnerId)
}

export function applyFeatureFlippingResponse(checkAllResponse: FeatureFlippingParam[]) {
  Object.keys(features).forEach((featureKey) => {
    const checkFeature = checkAllResponse.find((feature) => feature.id === featureKey)
    if (
      featureKey === `${getCurrentAppBrand()}:new-configuration` ||
      featureKey === `${getCurrentAppBrand()}:new-configuration-dev`
    )
      return
    features[featureKey]!.value = checkFeature ? checkFeature.active : false
  })
}

export interface FeatureFlipping {
  isDeprecatedAppTypeLocaleEnabled: Ref<boolean>
  isProductViewedEnabled: Ref<boolean>
  isNewSearchEnabled: Ref<boolean>
  updateFeatureFlipping: (partnerId?: number, membershipCode?: string) => Promise<void>
  isAnchoredArticlesEnabled: Ref<boolean>
  isSearchBarPrevisionnelEnabled: Ref<boolean>
  isShippingAddressEnabled: Ref<boolean>
  isRealtimeStocksEnabled: Ref<boolean>
  isPrestaServiceEnabled: Ref<boolean>
  isReorderDisabled: Ref<boolean>
  isReorderV2Enabled: Ref<boolean>
  isPortalEntriesEnabled: Ref<boolean>
  isSimilarProductsEnabled: Ref<boolean>
  isFrequentlyBoughtTogetherEnabled: Ref<boolean>
  isCopilotArcDashboardEnabled: Ref<boolean>
  isFilterOrdersByInvoicesEnabled: Ref<boolean>
  isCustomFiltersEnabled: Ref<boolean>
  isDefaultCustomFiltersEnabled: Ref<boolean>
  isNewConfigurationEnabled: Ref<boolean>
  isSearchStockV2Enabled: Ref<boolean>
  isSubscriptionsFeatureEnabled: Ref<boolean>
  isCancelConsignmentEnabled: Ref<boolean>
  useAllUserSegmentsForBestOffer: Ref<boolean>
  isMultiWishlistFeatureActivated: Ref<boolean>
  isWishlistFeatureActivated: Ref<boolean>
  isNewPdpOffersEnabled: Ref<boolean>
  isFlashOrderEnabled: Ref<boolean>
  isMillingStoreCapabilityEnabled: Ref<boolean>
  isDocumentAccessRbacEnabled: Ref<boolean>
  isNewCartPageFeatureActivated: Ref<boolean>
  isNewAssociationModeSelectorEnabled: Ref<boolean>
}

export function useFeatureFlipping(): FeatureFlipping {
  return {
    get isDeprecatedAppTypeLocaleEnabled() {
      return ref(getConfig('ALLOW_DEPRECATED_APP_TYPE_LOCALE') === 'yes')
    },
    get isProductViewedEnabled() {
      return isProductViewedEnabled
    },
    get isNewSearchEnabled() {
      return isNewSearchEnabled
    },
    get isAnchoredArticlesEnabled() {
      return isAnchoredArticlesEnabled
    },
    get isSearchBarPrevisionnelEnabled() {
      return isSearchBarPrevisionnelEnabled
    },
    get isShippingAddressEnabled() {
      return isShippingAddressEnabled
    },
    get isRealtimeStocksEnabled() {
      return isRealtimeStocksEnabled
    },
    get isPrestaServiceEnabled() {
      return isPrestaServiceEnabled
    },
    get isReorderDisabled() {
      return isReorderDisabled
    },
    get isReorderV2Enabled() {
      return isReorderV2Enabled
    },
    get isPortalEntriesEnabled() {
      return ref(getConfig('PORTAL_ENTRIES_ENABLED'))
    },
    get isSimilarProductsEnabled() {
      return isSimilarProductsEnabled
    },
    get isFrequentlyBoughtTogetherEnabled() {
      return isFrequentlyBoughtTogetherEnabled
    },
    get isCopilotArcDashboardEnabled() {
      return ref(getConfig('COPILOT_ARC_DASHBOARD_ENABLED') === 'yes')
    },
    get isFilterOrdersByInvoicesEnabled() {
      return ref(getConfig('FILTER_ORDERS_BY_INVOICES_FEATURE') === 'yes')
    },
    get isCustomFiltersEnabled() {
      return isCustomFiltersEnabled
    },
    get isDefaultCustomFiltersEnabled() {
      return isDefaultCustomFiltersEnabled
    },
    get isNewConfigurationEnabled() {
      return isNewConfigurationEnabled
    },
    get isSearchStockV2Enabled() {
      return isSearchStockV2Enabled
    },
    get isSubscriptionsFeatureEnabled() {
      return ref(getConfig('SUBSCRIPTIONS_ENABLED') === 'yes')
    },
    get isCancelConsignmentEnabled() {
      return isCancelConsignmentEnabled
    },
    get useAllUserSegmentsForBestOffer() {
      return useAllUserSegmentsForBestOffer
    },
    get isWishlistFeatureActivated() {
      return ref(getConfig('IS_WISHLIST_FEATURE_ACTIVATED') === 'yes')
    },
    get isMultiWishlistFeatureActivated() {
      return ref(getConfig('IS_MULTI_WISHLIST_FEATURE_ACTIVATED') === 'yes')
    },
    get isNewPdpOffersEnabled() {
      return isNewPdpOffersEnabled
    },
    get isFlashOrderEnabled() {
      return isFlashOrderEnabled
    },
    get isMillingStoreCapabilityEnabled() {
      return isMillingStoreCapabilityEnabled
    },
    get isDocumentAccessRbacEnabled() {
      return isDocumentAccessRbacEnabled
    },
    get isNewCartPageFeatureActivated() {
      return ref(getConfig('IS_NEW_CART_PAGE_FEATURE_ACTIVATED') === 'yes')
    },
    get isNewAssociationModeSelectorEnabled() {
      return isNewAssociationModeSelectorEnabled
    },
    updateFeatureFlipping: async (partnerId?: number, membershipCode?: string) => {
      if (!featureFlippingService) throw new Error('FeatureFlippingService is not initialized')

      try {
        // Temporary call, don't forget to remove when all cooperative will be migrated
        if (partnerId && membershipCode) {
          const api = createFrontAxiosClient(getConfig('OFFER_API_HOST'))
          const { data } = await api.get(`/v1/offers/${partnerId}/configuration-type`, {
            headers: {
              'X-Beneficiary-Id': membershipCode,
            },
          })
          const { type } = data
          features[`${getCurrentAppBrand()}:new-configuration`]!.value = type === 'new'
        }
        // End of temporary call
        const checkAllResponse = await featureFlippingService.checkAllFeatures(partnerId)
        applyFeatureFlippingResponse(checkAllResponse)
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
  }
}
